import { useEffect, useRef, useState } from "react";
import { showError } from "../../lib/feedback";

const MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4", "audio/mpeg"];

const EXTENSIONS: [string, string][] = [["webm", "webm"], ["ogg", "ogg"], ["mp4", "mp4"], ["mpeg", "mp3"], ["mp3", "mp3"]];

export const audioExtension = (mime: string) => EXTENSIONS.find(([part]) => mime.includes(part))?.[1] ?? "webm";

const supportedMimeType = () => MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";

// Grava do microfone. stop() devolve o áudio como File (com a extensão certa
// para o backend) ou null se nada foi gravado.
export const useAudioRecorder = (fileName: string) => {
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);

  const release = () => {
    recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    setRecording(false);
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      showError("mic-permission-denied");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = supportedMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      showError("mic-permission-denied");
    }
  };

  const stop = () =>
    new Promise<File | null>((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state !== "recording") {
        release();
        resolve(null);
        return;
      }
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      recorder.onstop = () => {
        const type = recorder.mimeType || chunks[0]?.type || "audio/webm";
        release();
        resolve(chunks.length ? new File(chunks, `${fileName}_${Date.now()}.${audioExtension(type)}`, { type }) : null);
      };
      recorder.stop();
    });

  // Sair da tela no meio da gravação libera o microfone.
  useEffect(() => () => recorderRef.current?.stream.getTracks().forEach((track) => track.stop()), []);

  return { recording, start, stop };
};
